import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonCard,
  IonCardContent,
  IonIcon,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonLabel,
  IonSpinner,
  IonSkeletonText,
  IonToast,
} from '@ionic/react';
import {
  closeOutline,
  callOutline,
  mailOutline,
  globeOutline,
  locationOutline,
  mapOutline,
  cardOutline,
  pencilOutline,
  checkmarkCircle,
  alertCircle,
  search,
} from 'ionicons/icons';
import {
  getWixBusinessProfile,
  updateWixBusinessSchedule,
} from '@rsmaids/shared/services/api/businessInfo';
import type { WixBusinessProfile, WixDayHours } from '@rsmaids/shared/services/api/businessInfo';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import styles from './BusinessProfile.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import i18n from '../i18n';

interface BusinessProfileProps {
  onClose?: () => void;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type EditHours = Record<DayKey, WixDayHours | null>;

const DAYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABELS: Record<DayKey, string> = {
  get monday() { return i18n.t('pages.BusinessProfile.monday'); }, get tuesday() { return i18n.t('pages.BusinessProfile.tuesday'); }, get wednesday() { return i18n.t('pages.BusinessProfile.wednesday'); },
  get thursday() { return i18n.t('pages.BusinessProfile.thursday'); }, get friday() { return i18n.t('pages.BusinessProfile.friday'); }, get saturday() { return i18n.t('pages.BusinessProfile.saturday'); }, get sunday() { return i18n.t('pages.BusinessProfile.sunday'); },
};

const DEFAULT_HOURS: WixDayHours = { open: '09:00', close: '17:00' };

const fmt12 = (t: string): string => {
  const [hStr, mStr] = t.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const hoursToEdit = (hours: WixBusinessProfile['hours']): EditHours =>
  Object.fromEntries(
    DAYS.map(d => [d, hours?.[d] ?? null])
  ) as EditHours;

const BusinessProfile: React.FC<BusinessProfileProps> = ({ onClose }) => {
  const { t: tr } = useTranslation();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardRef = useRef<HTMLIonRowElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);

  const [profile, setProfile] = useState<WixBusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editHours, setEditHours] = useState<EditHours | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    getWixBusinessProfile()
      .then(data => setProfile(data))
      .catch(err => console.error('[BusinessProfile]', err))
      .finally(() => setLoading(false));
  }, []);

  const startEdit = useCallback(() => {
    setEditHours(hoursToEdit(profile?.hours));
    setEditMode(true);
  }, [profile?.hours]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    setEditHours(null);
  }, []);

  const toggleDay = useCallback((day: DayKey, open: boolean) => {
    setEditHours(prev => prev ? {
      ...prev,
      [day]: open ? { ...DEFAULT_HOURS } : null,
    } : prev);
  }, []);

  const setTime = useCallback((day: DayKey, field: 'open' | 'close', value: string) => {
    setEditHours(prev => prev ? {
      ...prev,
      [day]: prev[day] ? { ...prev[day]!, [field]: value } : prev[day],
    } : prev);
  }, []);

  const saveHours = useCallback(async () => {
    if (!editHours) return;
    setSaving(true);
    try {
      await updateWixBusinessSchedule(editHours, profile?._scheduleId, profile?._masterEvents, profile?._masterRevisions, profile?._masterAnchorDates);
      setProfile(prev => prev ? { ...prev, hours: { ...editHours } } : prev);
      setEditMode(false);
      setEditHours(null);
      setToast({ message: tr('pages.BusinessProfile.businessHoursSaved'), type: 'success' });
    } catch (err) {
      console.error('[BusinessProfile] save hours', err);
      setToast({ message: tr('pages.BusinessProfile.failedToSavePleaseTryAgain'), type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [editHours, profile, tr]);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  const initials = profile?.name
    ? profile.name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const fullAddress = profile?.address
    ? [
        profile.address.line1,
        [profile.address.city, profile.address.state, profile.address.zip]
          .filter(Boolean).join(', '),
        profile.address.country && profile.address.country !== 'US'
          ? profile.address.country
          : undefined,
      ].filter(Boolean).join('\n')
    : null;

  const contactFields = [
    { icon: callOutline,     badgeClass: styles.iconBadgePrimary,   label: 'Phone',      value: profile?.phone },
    { icon: mailOutline,     badgeClass: styles.iconBadgeSecondary, label: 'Email',      value: profile?.email },
    { icon: globeOutline,    badgeClass: styles.iconBadgeTertiary,  label: 'Website',    value: profile?.website },
    { icon: cardOutline,     badgeClass: styles.iconBadgePrimary,   label: 'Company ID', value: profile?.companyId },
    { icon: locationOutline, badgeClass: styles.iconBadgeWarning,   label: 'Address',    value: fullAddress ?? undefined },
  ].filter(f => Boolean(f.value));

  return (
    <ModalWrapper>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={tr('pages.BusinessProfile.businessProfile')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton shape='round' className={styles.searchButton} style={{ visibility: 'hidden' }}>
            <IonIcon icon={search} />
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <IonGrid
            ref={containerRef}
            className={styles.container}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop)}
          >
            {loading ? (
              <>
                {/* Loading placeholders: fixed text (section titles, labels, day names, Edit) is rendered
                    for real; only business data is a bar. Hero mirrors TitleCard's markup. */}
                <IonRow className={styles.titleCardWrapper} aria-hidden="true">
                  <IonCol>
                    <IonCard className={styles.hero}>
                      <IonCardContent>
                        <IonRow className={styles.titleContainer}>
                          <IonCol size="auto" style={{ padding: 0 }}>
                            <IonSkeletonText animated className={`${styles.skeletonBar} ${styles.heroAvatar}`} style={{ borderRadius: '50%', margin: 0 }} />
                          </IonCol>
                          <IonCol style={{ padding: 0, textAlign: 'center' }}>
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '70%', height: 20, margin: '2px 0' }} />
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '90%', height: 13, margin: '4px auto 0' }} />
                          </IonCol>
                        </IonRow>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                </IonRow>

                <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`} aria-hidden="true">
                  <IonCardContent className={styles.section}>
                    <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.contactInformation')}</IonLabel>
                    {[
                      { icon: callOutline,     badgeClass: styles.iconBadgePrimary,   label: 'Phone' },
                      { icon: mailOutline,     badgeClass: styles.iconBadgeSecondary, label: 'Email' },
                      { icon: globeOutline,    badgeClass: styles.iconBadgeTertiary,  label: 'Website' },
                      { icon: locationOutline, badgeClass: styles.iconBadgeWarning,   label: 'Address' },
                    ].map(({ icon, badgeClass, label }) => (
                      <div key={label} className={styles.infoRow}>
                        <div className={`${styles.iconBadge} ${badgeClass}`}>
                          <IonIcon icon={icon} />
                        </div>
                        <div className={styles.infoText}>
                          <span className={styles.infoLabel}>{label}</span>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '55%', height: 13, margin: '1px 0' }} />
                        </div>
                      </div>
                    ))}
                  </IonCardContent>
                </IonCard>

                <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`} aria-hidden="true">
                  <IonCardContent className={styles.section}>
                    <div className={styles.sectionHeaderRow}>
                      <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.businessHours')}</IonLabel>
                      <button className={styles.editBtn} type="button" disabled>
                        <IonIcon icon={pencilOutline} />{tr('pages.BusinessProfile.edit')}
                      </button>
                    </div>
                    <div className={styles.hoursGrid}>
                      {DAYS.map(day => (
                        <div key={day} className={`${styles.hoursRow} ${day === 'saturday' || day === 'sunday' ? styles.weekend : ''}`}>
                          <span className={styles.dayLabel}>{DAY_LABELS[day]}</span>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 104, height: 13, margin: '1px 0' }} />
                        </div>
                      ))}
                    </div>
                  </IonCardContent>
                </IonCard>

                <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`} aria-hidden="true">
                  <IonCardContent className={styles.section}>
                    <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.serviceArea')}</IonLabel>
                    <div className={styles.areaWrap}>
                      {[96, 80, 112].map(w => (
                        <IonSkeletonText key={w} animated className={styles.skeletonBar} style={{ width: w, height: 26, margin: 0, borderRadius: 20 }} />
                      ))}
                    </div>
                  </IonCardContent>
                </IonCard>
              </>
            ) : (
              <>
                <IonRow ref={titleCardRef} className={styles.titleCardWrapper}>
                  <IonCol>
                    <TitleCard
                      title={profile?.name || tr('pages.BusinessProfile.businessProfile')}
                      subtitle={profile?.tagline}
                      subtitleClassName={styles.heroSubtitle}
                      cardClassName={styles.hero}
                      titleContainerClassName={styles.titleContainer}
                      titleClassName={styles.heroTitle}
                      titleAvatarClassName={styles.heroAvatar}
                      avatarUrl={profile?.logoUrl}
                      avatarInitials={initials}
                    />
                  </IonCol>
                </IonRow>

                {profile?.category && (
                  <div className={styles.categoryRow}>
                    <span className={styles.categoryBadge}>{translateDynamic(profile.category)}</span>
                  </div>
                )}

                {/* Contact Information */}
                <IonCard className={styles.sectionCard}>
                  <IonCardContent className={styles.section}>
                    <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.contactInformation')}</IonLabel>
                    {contactFields.length > 0 ? contactFields.map(({ icon, badgeClass, label, value }) => (
                      <div key={label} className={styles.infoRow}>
                        <div className={`${styles.iconBadge} ${badgeClass}`}>
                          <IonIcon icon={icon} />
                        </div>
                        <div className={styles.infoText}>
                          <span className={styles.infoLabel}>{label}</span>
                          <span className={styles.infoValue}>{value}</span>
                        </div>
                      </div>
                    )) : (
                      <p className={styles.emptyHint}>{tr('pages.BusinessProfile.noContactInfoConfigured')}</p>
                    )}
                  </IonCardContent>
                </IonCard>

                {/* Business Hours */}
                <IonCard className={styles.sectionCard}>
                  <IonCardContent className={styles.section}>
                    <div className={styles.sectionHeaderRow}>
                      <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.businessHours')}</IonLabel>
                      {!editMode && (
                        <button className={styles.editBtn} onClick={startEdit} type="button">
                          <IonIcon icon={pencilOutline} />{tr('pages.BusinessProfile.edit')}
                        </button>
                      )}
                    </div>

                    {editMode && editHours ? (
                      <div className={styles.hoursEdit}>
                        {DAYS.map(day => {
                          const hrs = editHours[day];
                          const isOpen = hrs !== null;
                          return (
                            <div key={day} className={styles.hoursEditRow}>
                              <span className={styles.dayLabel}>{DAY_LABELS[day]}</span>
                              <label className={styles.toggleWrap}>
                                <input
                                  type="checkbox"
                                  className={styles.toggleInput}
                                  checked={isOpen}
                                  onChange={e => toggleDay(day, e.target.checked)}
                                />
                                <span className={`${styles.toggleTrack} ${isOpen ? styles.toggleTrackOn : ''}`}>
                                  <span className={styles.toggleThumb} />
                                </span>
                                <span className={styles.toggleLabel}>{isOpen ? tr('pages.BusinessProfile.open') : tr('pages.BusinessProfile.closed')}</span>
                              </label>
                              {isOpen && (
                                <div className={styles.timePair}>
                                  <input
                                    type="time"
                                    className={styles.timeInput}
                                    value={hrs!.open}
                                    onChange={e => setTime(day, 'open', e.target.value)}
                                  />
                                  <span className={styles.timeSep}>–</span>
                                  <input
                                    type="time"
                                    className={styles.timeInput}
                                    value={hrs!.close}
                                    onChange={e => setTime(day, 'close', e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className={styles.editActions}>
                          <button type="button" className={styles.cancelBtn} onClick={cancelEdit} disabled={saving}>{tr('pages.BusinessProfile.cancel')}
                          </button>
                          <button type="button" className={styles.saveBtn} onClick={saveHours} disabled={saving}>
                            {saving
                              ? <IonSpinner name="bubbles" color="light" className={styles.savingSpinner} />
                              : tr('pages.BusinessProfile.save')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.hoursGrid}>
                        {DAYS.map(day => {
                          const hrs = profile?.hours?.[day];
                          const isWeekend = day === 'saturday' || day === 'sunday';
                          return (
                            <div key={day} className={`${styles.hoursRow} ${isWeekend ? styles.weekend : ''}`}>
                              <span className={styles.dayLabel}>{DAY_LABELS[day]}</span>
                              {hrs ? (
                                <span className={styles.hoursValue}>
                                  {fmt12(hrs.open)}&nbsp;–&nbsp;{fmt12(hrs.close)}
                                </span>
                              ) : (
                                <span className={styles.closedBadge}>{tr('pages.BusinessProfile.closed')}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </IonCardContent>
                </IonCard>

                {/* Service Area */}
                <IonCard className={styles.sectionCard}>
                  <IonCardContent className={styles.section}>
                    <IonLabel className={styles.sectionTitle}>{tr('pages.BusinessProfile.serviceArea')}</IonLabel>
                    {profile?.serviceArea?.length ? (
                      <div className={styles.areaWrap}>
                        {profile.serviceArea.map(area => (
                          <span key={area} className={styles.areaChip}>
                            <IonIcon icon={mapOutline} />
                            {area}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.emptyHint}>{tr('pages.BusinessProfile.noServiceAreasConfigured')}</p>
                    )}
                  </IonCardContent>
                </IonCard>
              </>
            )}
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>

      <IonToast
        isOpen={!!toast}
        message={toast?.message}
        duration={2500}
        onDidDismiss={() => setToast(null)}
        icon={toast?.type === 'success' ? checkmarkCircle : alertCircle}
      />
    </ModalWrapper>
  );
};

// Plain non-IonPage wrapper — this component is only ever shown inside an
// IonModal (see Manage.tsx), so it paints the page gradient itself once here
// instead of relying on an <IonPage> ancestor, matching EmailCompose.tsx's
// onClose-branch wrapper.
const ModalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.modalWrapper}>{children}</div>
);

export default BusinessProfile;
